// La puerta al agente desde el calendario — ADR-2026-10-06-el-agente-planifica-el-calendario.
//
// Con el azul de Atlas, no como un botón chico: planificar con el agente es la forma
// principal de llenar el calendario (Klaviyo y Cofounder en Mobbin), y armar post por post
// a mano es la alternativa. En el teléfono va arriba del mes; en la compu, la tarjeta va
// DEBAJO del calendario (ADR-2026-10-22-la-tarjeta-del-agente-va-debajo-del-calendario):
// por eso `version` dibuja una sola de las dos. La caja manda el pedido al chat como la de la
// home (`enviarMensaje` directo, ADR-2026-09-21-la-caja-de-la-home-es-la-del-agente): el
// usuario escribió y apretó enviar, así que el turno es suyo. El chat se abre al costado y
// el calendario queda a la vista mientras el agente lo llena.
//
// Aislado en su propio componente porque `useChatAtlas` se re-renderiza con cada token:
// así el calendario no se repinta mientras el agente escribe.
//
// En el teléfono es una BARRA de una línea y no la tarjeta: la tarjeta ocupaba la primera
// pantalla entera y el calendario quedaba abajo del pliegue. Los atajos van siempre a la
// vista, en una fila que se desliza adentro de la barra: escondidos detrás de un toque no
// los encontraba nadie — ADR-2026-10-07-el-calendario-en-el-telefono.
//
// En la prueba gratis la caja se ve igual, con un candado en el botón de enviar, y cualquier
// toque (la caja, enviar o un atajo) abre los planes en vez del chat
// (ADR-2026-11-02-planificar-el-calendario-es-de-plan-pago).

import { useState } from 'react';
import { ArrowUp, Lock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useChatAtlas, useChatAtlasCascaron } from '../../../chat-atlas';
import { useFuncionesAvanzadas } from '../../../hooks/useFuncionesAvanzadas';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { puedePlanificarElCalendarioConElAsistente } from '../../../lib/planCapabilities';
import { useCuentaShell } from '../../estilos/cuentaShell';

const CONTEXTO = { pantalla: 'el calendario de Instagram', ruta: '/instagram' };

export function PlanificarConElAgente({
  mesVisible,
  incompletos,
  vacio,
  version,
}: {
  /** Cuál dibujar: la barra del teléfono o la tarjeta de la compu. Van en lugares distintos. */
  version: 'telefono' | 'escritorio';
  /** "octubre de 2026": el mes que está mirando, para los atajos. */
  mesVisible: string;
  /** Posts del plan a los que les falta algo: suma el atajo de completarlos. */
  incompletos: number;
  /** El calendario no tiene ningún post: la tarjeta habla de empezar. */
  vacio: boolean;
}) {
  const navigate = useNavigate();
  const { abrirChat } = useChatAtlasCascaron();
  const chat = useChatAtlas();
  const { plan } = useFuncionesAvanzadas();
  const { isDevTester } = useUserGroup();
  const { abrirPricing, dentroDelShell } = useCuentaShell();
  const [texto, setTexto] = useState('');
  // Mientras el plan carga NO se muestra el candado: preferible que un gratis vea la caja un
  // instante a que un pago vea un candado que no le corresponde. Los dev testers pasan, como
  // en todos los candados.
  const bloqueado = !isDevTester && !puedePlanificarElCalendarioConElAsistente(plan);

  // Fuera del shell `abrirPricing` no hace nada: ahí va a la pantalla de planes.
  const abrirPlanes = () => (dentroDelShell ? abrirPricing('bloqueado') : navigate('/subscription'));

  const atajos = [
    `Llená lo que queda de ${mesVisible} con 3 posts por semana`,
    'Armame la semana que viene con mis últimas creaciones',
    ...(incompletos === 1 ? ['Completá el post al que le falta algo'] : []),
    ...(incompletos > 1 ? [`Completá los ${incompletos} posts a los que les falta algo`] : []),
  ];

  const enviar = (pedido: string) => {
    if (bloqueado) return abrirPlanes();
    const limpio = pedido.trim();
    if (!limpio) return;
    abrirChat({ contexto: CONTEXTO, vista: 'panel' });
    // Con un turno en curso, el pedido espera en la cola en vez de perderse.
    if (chat.streaming) chat.encolarMensaje(limpio);
    else chat.enviarMensaje(limpio);
    setTexto('');
  };

  return (
    <>
      {/* ── Teléfono: la barra ─────────────────────────────────────────────── */}
      {version === 'telefono' && (
        <section aria-label="Planificar con el asistente" className="md:hidden">
          <div className="rounded-2xl bg-gradient-to-r from-brand-primary to-[rgb(var(--brand-primary-rgb)_/_0.65)] p-1.5 shadow-[0_8px_20px_-10px_rgb(var(--brand-primary-rgb)_/_0.6)]">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                enviar(texto);
              }}
              className="flex items-center gap-2 rounded-xl bg-white py-1 pr-1 pl-3"
            >
              <Sparkles className="w-4 h-4 shrink-0 text-brand-primary" />
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                readOnly={bloqueado}
                onClick={bloqueado ? abrirPlanes : undefined}
                placeholder={vacio ? 'Pedile al asistente que arme tu mes' : 'Planificá con el asistente…'}
                aria-label="Qué querés que planifique el asistente"
                className="min-w-0 flex-1 bg-transparent py-2 text-[15px] text-brand-black outline-none placeholder:text-neutral-400"
              />
              <button
                type="submit"
                disabled={!bloqueado && !texto.trim()}
                aria-label={bloqueado ? 'Suscribite para planificar con el asistente' : 'Planificar'}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-primary text-white transition-opacity disabled:opacity-30"
              >
                {bloqueado ? <Lock className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
              </button>
            </form>
            {/* En una fila que se desliza: tres atajos apilados ocupaban media pantalla. */}
            <div className="-mx-1.5 mt-1.5 flex gap-2 overflow-x-auto px-1.5 pb-0.5 [scrollbar-width:none]">
              {atajos.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => enviar(a)}
                  className="shrink-0 rounded-full bg-white/15 py-1.5 px-3 text-xs font-medium text-white"
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Escritorio: la tarjeta ─────────────────────────────────────────── */}
      {version === 'escritorio' && (
        <section
          aria-label="Planificar con el asistente"
          className="relative hidden overflow-hidden rounded-3xl md:block bg-gradient-to-br from-brand-primary via-brand-primary-claro to-[rgb(var(--brand-primary-rgb)_/_0.65)] p-5 text-white shadow-[0_12px_32px_-12px_rgb(var(--brand-primary-rgb)_/_0.55)] sm:p-6"
        >
          {/* Brillos de fondo: dan profundidad sin una imagen que cargar. */}
          <span
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-2xl"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-white/[0.12] blur-2xl"
          />

          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
            <div className="min-w-0 lg:max-w-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 py-1 px-2.5 text-[11px] font-medium uppercase tracking-wide">
                <Sparkles className="w-3.5 h-3.5" /> Asistente de Atlas
              </span>
              <h2 className="mt-2 text-lg font-semibold leading-snug sm:text-xl">
                {vacio
                  ? 'Tu calendario está vacío. ¿Lo armamos juntos?'
                  : 'Planificá tu Instagram con el asistente'}
              </h2>
              <p className="mt-1 text-sm text-white/80">
                Elige piezas de tus creaciones, escribe cada texto y busca los mejores horarios. Vos revisás y
                aprobás antes de que quede en el calendario.
              </p>
            </div>

            <div className="min-w-0 flex-1">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  enviar(texto);
                }}
                className="flex items-center gap-2 rounded-2xl bg-white p-1.5 pl-4 shadow-sm focus-within:ring-4 focus-within:ring-white/30"
              >
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  readOnly={bloqueado}
                  onClick={bloqueado ? abrirPlanes : undefined}
                  placeholder="Ej: 4 posts por semana mostrando la colección nueva"
                  aria-label="Qué querés que planifique el asistente"
                  className="min-w-0 flex-1 bg-transparent py-2 text-sm text-brand-black outline-none placeholder:text-neutral-400"
                />
                <button
                  type="submit"
                  disabled={!bloqueado && !texto.trim()}
                  aria-label={bloqueado ? 'Suscribite para planificar con el asistente' : 'Planificar'}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-primary text-white transition-opacity disabled:opacity-30"
                >
                  {bloqueado ? <Lock className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                </button>
              </form>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {atajos.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => enviar(a)}
                    className="rounded-full bg-white/15 py-1.5 px-3 text-left text-xs font-medium text-white transition-colors hover:bg-white/25"
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
